import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../context/userContext";
import { extractErrorMessage } from "../utils/apiError";
import { setPageTitle } from "../utils/pageTitle";
import { toast } from "../utils/toast";
import {
  getPendingVerificationEmail,
  setPendingVerificationEmail,
  clearPendingVerificationEmail,
  getVerificationExpiresAt,
  setVerificationExpiresAt,
} from "../utils/auth";
import { SUPPORTED_LANGS, DEFAULT_LANG, normalizeLang, translations } from "../utils/i18n";

// After this many wrong tries in a row the backend permanently kills the
// code (even the correct one then gets rejected) — resend is the only way
// forward. The API itself doesn't say "locked" vs "wrong" in its response,
// so this is a local count purely to change the messaging.
const LOCKOUT_THRESHOLD = 5;
const RESEND_COOLDOWN_SECONDS = 30;
// Mirrors the backend's CODE_TTL (domains/user/otp.py). The server is the
// real source of truth — this only drives the visible countdown and an
// early UI block, a wrong/late code still just gets the normal 400.
const CODE_TTL_MS = 10 * 60 * 1000;

function formatCountdown(ms) {
  const totalSeconds = Math.ceil(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function VerifyEmailPage() {
  const { verifyEmail, resendVerification } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: langParam } = useParams();
  const language = normalizeLang(langParam);
  const tr = translations[language].verifyEmail;
  const emailFromState = location.state?.email;
  const autoResend = Boolean(location.state?.autoResend);
  const [email] = useState(() => emailFromState || getPendingVerificationEmail());
  const [code, setCode] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpResetToken, setOtpResetToken] = useState(0);
  // null until we actually know when the outstanding code dies — that's
  // either right away (a fresh code was just emailed by signup) or once
  // the auto-resend on the unverified-login path resolves.
  const [expiresAt, setExpiresAt] = useState(() => {
    if (autoResend) return null;
    if (emailFromState) return Date.now() + CODE_TTL_MS;
    return getVerificationExpiresAt() || Date.now() + CODE_TTL_MS;
  });
  const [now, setNow] = useState(Date.now());
  const autoResentRef = useRef(false);

  useEffect(() => {
    if (expiresAt) {
      setVerificationExpiresAt(expiresAt);
    }
  }, [expiresAt]);

  useEffect(() => {
    if (!expiresAt) return;
    setNow(Date.now());
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= expiresAt) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const remainingMs = expiresAt ? Math.max(0, expiresAt - now) : null;
  const expired = remainingMs === 0;

  useEffect(() => {
    setPageTitle("Verify Email");
  }, []);

  useEffect(() => {
    if (!SUPPORTED_LANGS.includes(langParam)) {
      navigate(`/verify-email/${DEFAULT_LANG}`, { replace: true });
    }
  }, [langParam, navigate]);

  useEffect(() => {
    if (!email) {
      navigate(`/signup/${language}`, { replace: true });
      return;
    }
    setPendingVerificationEmail(email);
  }, [email, language, navigate]);

  const handleResend = useCallback(async () => {
    setResending(true);
    try {
      await resendVerification(email);
      setAttempts(0);
      setCode("");
      setOtpResetToken((t) => t + 1);
      setExpiresAt(Date.now() + CODE_TTL_MS);
      toast.success(tr.resendSuccess);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? tr.resendRateLimited : extractErrorMessage(err));
    } finally {
      setResending(false);
    }
  }, [email, resendVerification, tr.resendRateLimited, tr.resendSuccess]);

  useEffect(() => {
    if (autoResend && email && !autoResentRef.current) {
      autoResentRef.current = true;
      toast.info(tr.unverifiedNotice);
      handleResend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResend, email, handleResend]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verifyEmail(email, code);
      clearPendingVerificationEmail();
      navigate(`/login/${language}`, { replace: true, state: { verified: true } });
    } catch (err) {
      const status = err?.response?.status;
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      setCode("");
      setOtpResetToken((t) => t + 1);
      if (status !== 400) {
        toast.error(extractErrorMessage(err));
      } else {
        toast.error(nextAttempts >= LOCKOUT_THRESHOLD ? tr.tooManyAttempts : tr.invalidCode);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!email) {
    return null;
  }

  return (
    <div
      lang={language}
      dir={language === "ar" ? "rtl" : "ltr"}
      className="page-narrow auth-page verify-email-page"
    >
      <div className="auth-brand">
        <Link to="/" className="auth-brand-mark">
          <img src="/logo-mark.png" alt="" className="brand-mark" />
          {translations[language].common.brandName}
        </Link>
      </div>
      <div className="auth-content">
        <div className="auth-card">
          <h1>{tr.title}</h1>
          <p className="subtitle">{tr.subtitle(email)}</p>
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label id="code-label">{tr.codeLabel}</label>
              <OtpInput
                key={otpResetToken}
                length={6}
                onChange={setCode}
                disabled={submitting || expired}
                autoFocus
                labelledBy="code-label"
              />
              {remainingMs !== null && (
                <p className={expired ? "otp-timer is-expired" : "otp-timer"}>
                  {expired ? tr.codeExpired : tr.expiresIn(formatCountdown(remainingMs))}
                </p>
              )}
            </div>
            <button type="submit" className="btn-full" disabled={submitting || expired || code.length !== 6}>
              {submitting ? tr.submitting : tr.submit}
            </button>
          </form>
          <p className="subtitle auth-footer">
            {tr.resendPrompt}{" "}
            <button
              type="button"
              className="link-inline"
              onClick={handleResend}
              disabled={resending || resendCooldown > 0}
            >
              {resending ? tr.resending : resendCooldown > 0 ? tr.resendCooldown(resendCooldown) : tr.resend}
            </button>
          </p>
        </div>
        <p className="subtitle auth-footer">
          <Link to={`/signup/${language}`} onClick={clearPendingVerificationEmail} className="muted-link">
            {tr.useDifferentEmail}
          </Link>
        </p>
      </div>
    </div>
  );
}

function OtpInput({ length, onChange, disabled, autoFocus, labelledBy }) {
  const [digits, setDigits] = useState(() => Array(length).fill(""));
  const inputsRef = useRef([]);

  useEffect(() => {
    onChange(digits.join(""));
  }, [digits, onChange]);

  function handleChange(index, e) {
    const raw = e.target.value.replace(/\D/g, "");
    const char = raw ? raw[raw.length - 1] : "";
    setDigits((prev) => {
      const next = [...prev];
      next[index] = char;
      return next;
    });
    if (char && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handlePaste(index, e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").split("").slice(0, length - index);
    if (!pasted.length) return;
    e.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      pasted.forEach((d, offset) => {
        next[index + offset] = d;
      });
      return next;
    });
    const lastIndex = Math.min(index + pasted.length, length) - 1;
    inputsRef.current[lastIndex]?.focus();
  }

  return (
    <div className="otp-boxes" role="group" aria-labelledby={labelledBy}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          className="otp-box"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
